import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { LogoComponent } from '../../../../../shared/components/logo/logo.component';
import { Router, RouterLink } from '@angular/router';
import { LoggedUserStateService } from '../../../../services/logged-user-state.service';
import { SelectModule } from 'primeng/select';
import { TranslationStateService } from '../../../../../shared/services/translation-state.service';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../../../services/auth.service';
import { AccessTokenService } from '../../../../services/access-token.service';

@Component({
  selector: 'app-navbar',
  imports: [
    TranslateModule,
    FormsModule,
    LogoComponent,
    RouterLink,
    SelectModule,
    ButtonModule,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent {
  @Output() loading = new EventEmitter<boolean>();

  private el = inject(ElementRef);
  private loggedUserService = inject(LoggedUserStateService);
  private translationStateService = inject(TranslationStateService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private accessTokenService = inject(AccessTokenService);
  private loggedUserStateService = inject(LoggedUserStateService);

  userData = this.loggedUserService.userData;

  languageSelected: string = this.translationStateService.locale();

  isMobileOpen = signal(false);

  openDropdownId = signal<string | null>(null);

  setLanguage() {
    this.translationStateService.setLanguage(this.languageSelected);
  }

  toggleMobile() {
    const next = !this.isMobileOpen();
    this.isMobileOpen.set(next);
    if (!next) this.openDropdownId.set(null);
  }

  toggleDropdown(id: string, event?: Event) {
    event?.preventDefault();
    this.openDropdownId.set(this.openDropdownId() === id ? null : id);
  }

  closeAll() {
    this.isMobileOpen.set(false);
    this.openDropdownId.set(null);
  }

  // --- A11y / outside-click handling ---
  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    const target = ev.target as HTMLElement;
    // ignore clicks inside the navbar
    if (this.el.nativeElement.contains(target)) return;
    this.closeAll();
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    this.closeAll();
  }

  scrollToElement(elementId: string): void {
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }

  isRouterActive(route: string): boolean {
    return this.router.isActive(route, {
      paths: 'exact',
      queryParams: 'exact',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  logOut(): void {
    const token = this.accessTokenService.getAccessToken();
    if (token) {
      this.loading.emit(true);

      this.authService
        .logOut({ token, tokenTypeHint: 'access_token' })
        .subscribe({
          next: () => {
            this.loggedUserStateService.clear();
            this.accessTokenService.clearSession();

            this.openDropdownId.set(null);
            if (this.router.url !== '/home') {
              this.router.navigate(['/home'], { replaceUrl: true });
            }
            this.loading.emit(false);
          },
          error: (e) => {
            this.loading.emit(false);
          },
        });
    }
  }
}
